/**
 * Application entry — wires the reducer, components, and side effects.
 *
 * Architecture:
 *   - Single AppState held in this module's closure
 *   - dispatch(action) → reduce(state, action) → state' → updateAll(state')
 *   - Components are pure-DOM functions: render once, update on each state change
 *   - Side effects (loading minima, PDF generation) are scheduled by comparing
 *     the previous flow.tag to the new one inside dispatch
 *
 * Boot sequence:
 *   1. Load practice info from localStorage (self-heals on corruption)
 *   2. Build initial state with that practice info
 *   3. Mount all components into #app
 *   4. Run a first updateAll(state) to render the initial state
 *
 * The PDF side effect dynamically imports `pdf-generator-browser` to keep
 * pdf-lib + fontkit + the embedded fonts out of the initial bundle.
 */

import "./styles/global.css";

// DM Sans body font — four weights via @fontsource. Vite resolves the WOFF2
// references inside each per-weight CSS file and self-hosts them under
// /assets/, keeping the page CSP-self-only. Display font (ITC Avant Garde
// Book) loads from src/assets/fonts/ via the @font-face in global.css.
import "@fontsource/dm-sans/400.css";
import "@fontsource/dm-sans/400-italic.css";
import "@fontsource/dm-sans/700.css";
import "@fontsource/dm-sans/700-italic.css";

import { adoptAbout } from "./about";
import { createFooter } from "./components/footer";
import { createForm } from "./components/form";
import { createHeader } from "./components/header";
import { createHero } from "./components/hero";
import { createInlineCta } from "./components/inline-cta";
import { createPracticeSetup } from "./components/practice-setup";
import { createResults } from "./components/results";
import { createSecondaryRow } from "./components/secondary-row";
import { el } from "./dom";
import { createPdfLoader, whenIdle } from "./pdf-loader";
import { filenameFor, friendlyPdfError, triggerDownload } from "./pdf-download";
import { loadPracticeInfo } from "./practice-info";
import { type Action, type AppState, initialState, reduce } from "./state";

import "./main.css";

// -----------------------------------------------------------------------------
// Boot

const root = document.getElementById("app");
if (!root) {
  throw new Error("cvai-tools: missing #app mount point in index.html");
}

const loaded = loadPracticeInfo();
const initialPractice = loaded.kind === "ok" ? loaded.info : null;

let state: AppState = initialState(initialPractice);

// Forward declarations so component dispatches can call into our dispatcher.
const dispatch: (action: Action) => void = (action) => {
  const prev = state;
  const next = reduce(prev, action);
  state = next;
  updateAll(next);
  runSideEffects(prev, next);
};

// -----------------------------------------------------------------------------
// Components

const header = createHeader();
const hero = createHero();
const form = createForm({ dispatch });
const results = createResults({ dispatch });
const inlineCta = createInlineCta();
const secondaryRow = createSecondaryRow();
const footer = createFooter({ dispatch });
const practiceSetup = createPracticeSetup({ dispatch });

const calcSection = el(
  "section",
  { class: "container calc-layout", "aria-labelledby": "hero-title" },
  el("div", { class: "calc-layout__column" }, form.el),
  el("div", { class: "calc-layout__column" }, results.el),
);

const mainEl = el(
  "main",
  { class: "main", id: "main-content" },
  hero.el,
  calcSection,
  inlineCta.el,
  secondaryRow.el,
);

// The static about block from index.html joins <main>, above the footer.
adoptAbout(document, mainEl);

// Replace any prior contents (Day 1 hello stub) with the real app.
root.replaceChildren(header.el, mainEl, footer.el, practiceSetup.el);

// -----------------------------------------------------------------------------
// Update fan-out

function updateAll(s: AppState): void {
  form.update(s);
  results.update(s);
  footer.update(s);
  practiceSetup.update(s);
  // Header, hero, inline-cta, foundations-banner, secondary-row are pure
  // presentational with no state subscriptions — no update() to call.
}

// -----------------------------------------------------------------------------
// Side effects: scheduled by detecting flow.tag transitions

function runSideEffects(prev: AppState, next: AppState): void {
  const before = prev.flow.tag;
  const after = next.flow.tag;

  // Calculation has a 200ms perceived-latency floor: even though the math is
  // microseconds, an instant flicker between "Calculate" → results feels jumpy.
  // The 200ms reads as confidence, not lag.
  if (before !== "calculating" && after === "calculating") {
    window.setTimeout(() => dispatch({ type: "calculate-completed" }), 200);
  }

  // Generating PDF: real pdf-lib invocation behind a dynamic import.
  if (before !== "generating-pdf" && after === "generating-pdf") {
    void generatePdf();
  }
}

// Dynamic import keeps pdf-lib + fontkit + the font URLs out of the initial
// bundle. The lazy chunk (and the fonts) are warm-loaded once the page is idle,
// so a tab left open across a deploy still has them; see pdf-loader.ts.
const pdfLoader = createPdfLoader(() => import("./pdf-generator-browser"));

async function generatePdf(): Promise<void> {
  try {
    if (state.flow.tag !== "generating-pdf") {
      throw new Error("generatePdf called outside generating-pdf state");
    }
    if (state.practice === null) {
      throw new Error("Practice info missing — soft gate should have caught this");
    }
    // Usually already resolved: the chunk was warm-loaded after page load.
    const { generatePdf: realGeneratePdf } = await pdfLoader.load();
    const bytes = await realGeneratePdf({
      babyName: state.flow.values.babyName,
      results: state.flow.results,
      practice: state.practice,
      measuredAt: new Date(),
    });
    triggerDownload(bytes, filenameFor(state.flow.values.babyName));
    dispatch({ type: "pdf-completed" });
  } catch (err) {
    // Log the raw error so a "PDF didn't work" bug report is debuggable from
    // devtools — the surfaced message is summarized for the parent-DC, the
    // console keeps the full stack.
    console.error("PDF generation failed:", err);
    const message = friendlyPdfError(err);
    dispatch({ type: "pdf-failed", message });
  }
}

// -----------------------------------------------------------------------------
// Initial render

updateAll(state);

whenIdle(() => {
  pdfLoader.warm();
  void pdfLoader
    .load()
    .then((mod) => mod.preloadFonts())
    .catch(() => undefined);
});

// Build tag for round-trip verification on Cloudflare Pages deploys.
console.warn(`cvai-tools build ${import.meta.env.MODE} ${new Date().toISOString()}`);
